export function serializeRegistrationCredential(
  credential: PublicKeyCredential,
): string {
  if (typeof (credential as any).toJSON === 'function') {
    try {
      const credentialJsonObject = (credential as any).toJSON();
      if (isValidRegistrationJson(credentialJsonObject)) {
        return JSON.stringify(credentialJsonObject);
      }
    } catch {
      // Fall through to manual serialization
    }
  }

  return manualSerializeRegistration(credential);
}

export function serializeAuthenticationCredential(
  credential: PublicKeyCredential,
): string {
  if (typeof (credential as any).toJSON === 'function') {
    try {
      const credentialJsonObject = (credential as any).toJSON();
      if (isValidAuthenticationJson(credentialJsonObject)) {
        return JSON.stringify(credentialJsonObject);
      }
    } catch {
      // Fall through to manual serialization
    }
  }

  return manualSerializeAuthentication(credential);
}

// Some browsers produce non-conformant toJSON() output with standard base64
// encoding (+, /, =) instead of base64url, or missing required fields.
function isValidRegistrationJson(
  credentialJsonObject: Record<string, any>,
): boolean {
  const rawId = credentialJsonObject.rawId;
  const attestationObject = credentialJsonObject.response?.attestationObject;
  const clientDataJSON = credentialJsonObject.response?.clientDataJSON;

  if (!rawId || !attestationObject || !clientDataJSON) {
    return false;
  }
  if (!credentialJsonObject.clientExtensionResults) {
    return false;
  }
  if (
    containsNonBase64UrlChars(rawId) ||
    containsNonBase64UrlChars(attestationObject) ||
    containsNonBase64UrlChars(clientDataJSON)
  ) {
    return false;
  }

  return true;
}

// Some browsers produce non-conformant toJSON() output with standard base64
// encoding (+, /, =) instead of base64url, or missing required fields.
function isValidAuthenticationJson(
  credentialJsonObject: Record<string, any>,
): boolean {
  const rawId = credentialJsonObject.rawId;
  const authenticatorData = credentialJsonObject.response?.authenticatorData;
  const clientDataJSON = credentialJsonObject.response?.clientDataJSON;
  const signature = credentialJsonObject.response?.signature;

  if (!rawId || !authenticatorData || !clientDataJSON || !signature) {
    return false;
  }
  if (!credentialJsonObject.clientExtensionResults) {
    return false;
  }
  if (
    containsNonBase64UrlChars(rawId) ||
    containsNonBase64UrlChars(authenticatorData) ||
    containsNonBase64UrlChars(clientDataJSON) ||
    containsNonBase64UrlChars(signature)
  ) {
    return false;
  }

  return true;
}

function containsNonBase64UrlChars(value: string): boolean {
  return /[+/=]/.test(value);
}

function manualSerializeRegistration(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAttestationResponse;

  const responseJson: Record<string, unknown> = {
    clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
    attestationObject: arrayBufferToBase64url(response.attestationObject),
  };

  if (typeof response.getTransports === 'function') {
    responseJson.transports = response.getTransports();
  }
  if (typeof response.getAuthenticatorData === 'function') {
    responseJson.authenticatorData = arrayBufferToBase64url(
      response.getAuthenticatorData(),
    );
  }
  if (typeof response.getPublicKeyAlgorithm === 'function') {
    responseJson.publicKeyAlgorithm = response.getPublicKeyAlgorithm();
  }
  if (typeof response.getPublicKey === 'function') {
    const publicKey = response.getPublicKey();
    if (publicKey) {
      responseJson.publicKey = arrayBufferToBase64url(publicKey);
    }
  }

  return JSON.stringify({
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    response: responseJson,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: Object.fromEntries(
      Object.entries(credential.getClientExtensionResults()).map(
        ([key, value]) => [
          key,
          value instanceof ArrayBuffer ? arrayBufferToBase64url(value) : value,
        ],
      ),
    ),
    type: credential.type,
  });
}

function manualSerializeAuthentication(
  credential: PublicKeyCredential,
): string {
  const response = credential.response as AuthenticatorAssertionResponse;

  const responseJson: Record<string, unknown> = {
    clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
    authenticatorData: arrayBufferToBase64url(response.authenticatorData),
    signature: arrayBufferToBase64url(response.signature),
  };

  if (response.userHandle) {
    responseJson.userHandle = arrayBufferToBase64url(response.userHandle);
  }

  return JSON.stringify({
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    response: responseJson,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: Object.fromEntries(
      Object.entries(credential.getClientExtensionResults()).map(
        ([key, value]) => [
          key,
          value instanceof ArrayBuffer ? arrayBufferToBase64url(value) : value,
        ],
      ),
    ),
    type: credential.type,
  });
}

function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
